import { CareOSLoader } from '@/components/ui/careos-loader';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePatientRole } from '@/hooks/usePatientRole';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TaskFeedItem } from '@/components/dashboard/TaskFeedItem';
import { PhotoViewerModal } from '@/components/ui/photo-viewer-modal';
import { SignedImage } from '@/components/ui/signed-image';
import { MapPicker } from '@/components/ui/map-picker';
import {
  Clock,
  CheckCircle2,
  AlertTriangle,
  Activity,
  FileText,
  MapPin,
  Navigation,
  ImageOff,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format, differenceInHours, differenceInMinutes, isToday } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import i18n from '@/i18n';


interface ShiftData {
  id: string;
  check_in_at: string;
  check_out_at: string | null;
  status: string;
  check_in_photo_url: string | null;
  check_out_photo_url: string | null;
  check_in_latitude: number | null;
  check_in_longitude: number | null;
  notes: string | null;
  ai_summary: string | null;
  ai_summary_generated_at: string | null;
  caregiver?: {
    full_name: string | null;
  } | null;
}

interface TaskExecution {
  id: string;
  executed_at: string;
  is_alert: boolean;
  is_late: boolean;
  notes: string | null;
  photo_url: string | null;
  vital_value: number | null;
  vital_value_secondary: number | null;
  food_acceptance: string | null;
  is_sporadic: boolean;
  task_id: string | null;
  adhoc_task_type: string | null;
  adhoc_name: string | null;
  adhoc_vital_type: string | null;
  task: {
    name: string;
    task_type: string;
    vital_type?: string | null;
    unit: string | null;
    incident_type?: string | null;
  };
  executor?: {
    full_name: string | null;
    avatar_url?: string | null;
  } | null;
  reactions_count?: number;
  comments_count?: number;
  reactions?: Array<{ id: string; emoji: string; user_id: string }>;
}

interface Handoff {
  id: string;
  text_summary: string | null;
  tasks_completed: number;
  tasks_total: number;
  sporadic_meds_given: number;
  incidents_count: number;
  created_at: string;
}

interface PatientData {
  id: string;
  residence_latitude: number | null;
  residence_longitude: number | null;
}

// Haversine formula to calculate distance between two coordinates in meters
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }
  return `${(meters / 1000).toFixed(1)} km`;
}

export default function ShiftDetail() {
  const { patientId, shiftId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isOwner } = usePatientRole(patientId);
  const { settings: systemSettings } = useSystemSettings();

  const [shift, setShift] = useState<ShiftData | null>(null);
  const [patient, setPatient] = useState<PatientData | null>(null);
  const [executions, setExecutions] = useState<TaskExecution[]>([]);
  const [instances, setInstances] = useState<any[]>([]);
  const [handoff, setHandoff] = useState<Handoff | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [adjacent, setAdjacent] = useState<{
    prev: { id: string; check_in_at: string } | null;
    next: { id: string; check_in_at: string } | null;
  }>({ prev: null, next: null });

  const dateLocale = i18n.language === 'pt-BR' ? ptBR : enUS;

  useEffect(() => {
    if (shiftId && patientId) {
      fetchShiftData();
    }
  }, [shiftId, patientId]);

  const fetchShiftData = async () => {
    setIsLoading(true);
    setShift(null);
    setExecutions([]);
    setInstances([]);
    setHandoff(null);
    setAdjacent({ prev: null, next: null });
    try {
      // Fetch shift
      const { data: shiftData } = await supabase
        .from('shifts')
        .select('*')
        .eq('id', shiftId)
        .single();

      if (!shiftData) {
        setShift(null);
        setIsLoading(false);
        return;
      }

      // Fetch caregiver profile separately to avoid join issues/RLS complexities
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('user_id', shiftData.caregiver_id)
        .maybeSingle();

      setShift({
        ...shiftData,
        caregiver: profileData
      });

      // Fetch patient for residence location
      const { data: patientData } = await supabase
        .from('patients')
        .select('id, residence_latitude, residence_longitude')
        .eq('id', patientId)
        .single();

      setPatient(patientData);

      // Fetch adjacent shifts (previous/next) for the same patient
      const [prevRes, nextRes] = await Promise.all([
        supabase
          .from('shifts')
          .select('id, check_in_at')
          .eq('patient_id', patientId)
          .lt('check_in_at', shiftData.check_in_at)
          .order('check_in_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('shifts')
          .select('id, check_in_at')
          .eq('patient_id', patientId)
          .gt('check_in_at', shiftData.check_in_at)
          .order('check_in_at', { ascending: true })
          .limit(1)
          .maybeSingle(),
      ]);
      setAdjacent({ prev: prevRes.data || null, next: nextRes.data || null });


      // Fetch task instances for this shift (to count planned tasks)
      const { data: instancesData } = await supabase
        .from('shift_task_instances')
        .select(`
          id,
          status,
          scheduled_for,
          task_template_id,
          execution_id
        `)
        .eq('shift_id', shiftId);

      let enrichedInstances: any[] = instancesData || [];
      if (instancesData && instancesData.length > 0) {
        const templateIds = [
          ...new Set(instancesData.map(i => i.task_template_id).filter(Boolean)),
        ] as string[];
        if (templateIds.length > 0) {
          const { data: templateTasks } = await supabase
            .from('care_plan_tasks')
            .select('id, name, task_type, unit, vital_type')
            .in('id', templateIds);
          const templateMap = new Map(templateTasks?.map(t => [t.id, t]) || []);
          enrichedInstances = instancesData.map(i => ({
            ...i,
            task: i.task_template_id ? templateMap.get(i.task_template_id) || null : null,
          }));
        }
      }
      setInstances(enrichedInstances);

      // Fetch task executions for this shift
      const { data: executionsData } = await supabase
        .from('task_executions')
        .select('id, executed_at, is_alert, is_late, notes, vital_value, vital_value_secondary, food_acceptance, task_id, photo_url, is_sporadic, executed_by, adhoc_task_type, adhoc_name, adhoc_vital_type')
        .eq('shift_id', shiftId)
        .order('executed_at', { ascending: false });

      if (executionsData && executionsData.length > 0) {
        // Filter out null task_ids before querying care_plan_tasks
        const taskIds = [...new Set(executionsData.map(e => e.task_id).filter(Boolean))] as string[];
        const executorIds = [...new Set(executionsData.map(e => e.executed_by))];

        let tasksMap = new Map<string, { id: string; name: string; task_type: string; unit: string | null; vital_type: string | null; incident_type: string | null }>();
        if (taskIds.length > 0) {
          const { data: tasksData } = await supabase
            .from('care_plan_tasks')
            .select('id, name, task_type, unit, vital_type, incident_type')
            .in('id', taskIds);
          tasksMap = new Map(tasksData?.map(t => [t.id, t]) || []);
        }

        const [profilesDataResult, reactionsResult, commentsResult] = await Promise.all([
          supabase
            .from('profiles')
            .select('user_id, full_name, avatar_url')
            .in('user_id', executorIds),
          supabase
            .from('task_reactions')
            .select('task_execution_id, id, emoji, user_id')
            .in('task_execution_id', executionsData.map(e => e.id)),
          supabase
            .from('task_comments')
            .select('task_execution_id')
            .in('task_execution_id', executionsData.map(e => e.id))
        ]);

        const profilesMap = new Map((profilesDataResult.data || []).map(p => [p.user_id, p]));

        // Group reactions and comments
        const reactionsCountMap = new Map<string, number>();
        const commentsCountMap = new Map<string, number>();
        const reactionsMap = new Map<string, Array<{ id: string; emoji: string; user_id: string }>>();

        reactionsResult.data?.forEach(r => {
          const currentCount = reactionsCountMap.get(r.task_execution_id) || 0;
          reactionsCountMap.set(r.task_execution_id, currentCount + 1);

          const currentReactions = reactionsMap.get(r.task_execution_id) || [];
          currentReactions.push({ id: r.id, emoji: r.emoji, user_id: r.user_id });
          reactionsMap.set(r.task_execution_id, currentReactions);
        });

        commentsResult.data?.forEach(c => {
          const current = commentsCountMap.get(c.task_execution_id) || 0;
          commentsCountMap.set(c.task_execution_id, current + 1);
        });

        const enrichedExecutions: TaskExecution[] = executionsData.map(e => {
          // Use task from care_plan_tasks if available, otherwise use adhoc fields
          const task = e.task_id ? tasksMap.get(e.task_id) : null;

          return {
            ...e,
            is_sporadic: e.is_sporadic || false,
            is_late: e.is_late || false,
            adhoc_task_type: e.adhoc_task_type,
            task: task ? {
              name: task.name,
              task_type: task.task_type,
              vital_type: task.vital_type,
              unit: task.unit,
              incident_type: task.incident_type
            } : {
              name: e.adhoc_name || 'Tarefa Avulsa',
              task_type: e.adhoc_task_type || 'other',
              vital_type: e.adhoc_vital_type || null,
              unit: null,
              incident_type: null
            },
            executor: profilesMap.get(e.executed_by) || null,
            reactions_count: reactionsCountMap.get(e.id) || 0,
            comments_count: commentsCountMap.get(e.id) || 0,
            reactions: reactionsMap.get(e.id) || []
          };
        });

        setExecutions(enrichedExecutions);
      }

      // Fetch handoff if exists
      const { data: handoffData } = await supabase
        .from('handoffs')
        .select('*')
        .eq('shift_id', shiftId)
        .maybeSingle();

      setHandoff(handoffData);

    } catch (error) {
      console.error('Error fetching shift data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (start: string, end: string | null) => {
    const endDate = end ? new Date(end) : new Date();
    const hours = differenceInHours(endDate, new Date(start));
    const minutes = differenceInMinutes(endDate, new Date(start)) % 60;
    return `${hours}h ${minutes}min`;
  };

  // Calculate distance from residence - only for owners
  const getDistanceInfo = () => {
    if (!isOwner) return null; // Non-owners cannot see location data
    if (!shift?.check_in_latitude || !shift?.check_in_longitude) return null;
    if (!patient?.residence_latitude || !patient?.residence_longitude) return null;

    const distance = calculateDistance(
      shift.check_in_latitude,
      shift.check_in_longitude,
      patient.residence_latitude,
      patient.residence_longitude
    );

    const allowedRadius = systemSettings.allowed_checkin_radius;

    return {
      distance,
      formatted: formatDistance(distance),
      isNearby: distance <= allowedRadius,
      isOutsideRadius: distance > allowedRadius,
    };
  };

  const distanceInfo = getDistanceInfo();

  if (isLoading) {
    return (
      <AppLayout title={t('shiftDetail.title')} showBack>
        <div className="flex items-center justify-center h-64">
          <CareOSLoader size="lg" />
        </div>
      </AppLayout>
    );
  }

  if (!shift) {
    return (
      <AppLayout title={t('shiftDetail.title')} showBack>
        <div className="p-4 text-center text-muted-foreground">
          {t('shiftDetail.notFound')}
        </div>
      </AppLayout>
    );
  }

  // Final KPI Logic
  const totalCount = instances.length;
  const completedCount = instances.filter(i => i.status === 'completed').length;
  const adherencePercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : null;
  const lateTasksCount =
    instances.filter(i => i.status === 'late').length +
    executions.filter(e => e.is_late).length;

  const incidentsCount = executions.filter(e => e.adhoc_task_type === 'incident').length;
  const sporadicMedsCount = executions.filter(e =>
    e.adhoc_task_type === 'sporadic_medication' ||
    (e.task_id && e.is_sporadic)
  ).length;
  const adhocOtherCount = executions.filter(e =>
    !e.task_id &&
    e.adhoc_task_type !== 'incident' &&
    e.adhoc_task_type !== 'sporadic_medication'
  ).length;

  const notExecutedInstances = instances
    .filter(i => i.status !== 'completed' && !i.execution_id)
    .sort((a, b) =>
      new Date(a.scheduled_for).getTime() - new Date(b.scheduled_for).getTime()
    );

  return (
    <AppLayout
      title={t('shiftDetail.title')}
      showBack
      onBack={() => navigate(`/app/family/${patientId}`)}
    >
      <div className="p-4 space-y-4 pb-24">
        {/* Shift Navigation */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="icon"
                disabled={!adjacent.prev}
                aria-label={t('shiftDetail.previousShift')}
                onClick={() =>
                  adjacent.prev &&
                  navigate(`/app/family/${patientId}/shift/${adjacent.prev.id}`)
                }
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>

              <div className="text-center">
                <div className="flex items-center justify-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  <span className="font-semibold">
                    {isToday(new Date(shift.check_in_at))
                      ? t('familyDashboard.today')
                      : format(new Date(shift.check_in_at), "EEEE", { locale: dateLocale })}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(shift.check_in_at), "PPP '·' HH:mm", { locale: dateLocale })}
                </p>
              </div>

              <Button
                variant="ghost"
                size="icon"
                disabled={!adjacent.next}
                aria-label={t('shiftDetail.nextShift')}
                onClick={() =>
                  adjacent.next &&
                  navigate(`/app/family/${patientId}/shift/${adjacent.next.id}`)
                }
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Shift Header */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-lg font-bold">
                  {format(new Date(shift.check_in_at), "EEEE, d MMMM", { locale: dateLocale })}
                </p>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <p className="text-sm">
                    {format(new Date(shift.check_in_at), 'HH:mm')}
                    {shift.check_out_at && ` - ${format(new Date(shift.check_out_at), 'HH:mm')}`}
                    <span className="mx-1">•</span>
                    Total: {formatDuration(shift.check_in_at, shift.check_out_at)}
                  </p>
                </div>
              </div>
              <Badge
                variant={shift.status === 'active' ? 'default' : 'secondary'}
                className={
                  shift.status === 'active'
                    ? undefined
                    : 'bg-success text-success-foreground hover:bg-success/80 border-transparent'
                }
              >
                {shift.status === 'active' ? t('shiftDetail.inProgress') : t('shiftDetail.completed')}
              </Badge>
            </div>

            {/* Standardized KPI Grid - 7 metrics */}
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2">
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-xl font-bold text-foreground">
                    {totalCount}
                  </div>
                  <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                    {t('dashboard.plannedTasks')}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-xl font-bold text-foreground">
                    {completedCount}
                  </div>
                  <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                    {t('dashboard.executedTasks')}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-xl font-bold text-foreground">
                    {adherencePercent !== null ? `${adherencePercent}%` : '-'}
                  </div>
                  <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                    {t('dashboard.completion')}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-xl font-bold text-foreground">
                    {sporadicMedsCount}
                  </div>
                  <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                    {t('dashboard.sporadicMeds')}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className={`text-xl font-bold ${incidentsCount > 0 ? 'text-destructive' : 'text-foreground'}`}>
                    {incidentsCount}
                  </div>
                  <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                    {t('dashboard.incidents')}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className={`text-xl font-bold ${lateTasksCount > 0 ? 'text-warning' : 'text-foreground'}`}>
                    {lateTasksCount}
                  </div>
                  <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                    {t('dashboard.lateTasks')}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-xl font-bold text-foreground">
                    {adhocOtherCount}
                  </div>
                  <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                    {t('dashboard.adhocTasks')}
                  </p>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        {/* Check-in Location Map */}
        {shift.check_in_latitude && shift.check_in_longitude && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {t('shiftDetail.checkInLocation')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="rounded-lg overflow-hidden border">
                {distanceInfo?.isOutsideRadius && patient?.residence_latitude && patient?.residence_longitude ? (
                  <MapPicker
                    latitude={shift.check_in_latitude}
                    longitude={shift.check_in_longitude}
                    primaryColor="red"
                    primaryPopup={`<strong>${t('shiftDetail.mapCheckInLocation')}</strong><br/>${distanceInfo.formatted} ${t('shiftDetail.mapOutsideRadiusHint')}`}
                    secondaryLatitude={patient.residence_latitude}
                    secondaryLongitude={patient.residence_longitude}
                    secondaryColor="green"
                    secondaryPopup={`<strong>${t('shiftDetail.mapPatientResidence')}</strong>`}
                    readOnly
                  />
                ) : (
                  <MapPicker
                    latitude={shift.check_in_latitude}
                    longitude={shift.check_in_longitude}
                    readOnly
                  />
                )}
              </div>

              {distanceInfo && (
                <div className={`flex items-center gap-2 p-2 rounded-lg ${distanceInfo.isNearby ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
                  <Navigation className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {t('shiftDetail.distanceFromResidence')}: {distanceInfo.formatted}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* AI Summary for Family */}
        {shift.ai_summary && (
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-primary">
                <Sparkles className="w-4 h-4" />
                {t('shiftDetail.aiSummary')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm leading-relaxed">
                {shift.ai_summary}
              </p>
              {shift.ai_summary_generated_at && (
                <p className="text-xs text-muted-foreground mt-3">
                  {t('shiftDetail.generatedAt')}: {format(new Date(shift.ai_summary_generated_at), "dd/MM/yyyy HH:mm", { locale: dateLocale })}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Handoff Summary */}
        {handoff && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {t('shiftDetail.handoffSummary')} {t('shiftDetail.reportedBy')}: {shift.caregiver?.full_name || t('shiftDetail.caregiver')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {handoff.text_summary ? (
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {handoff.text_summary}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  Sem relato descritivo para este plantão.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Not Executed Tasks */}
        {notExecutedInstances.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3">
              {t('shiftDetail.notExecutedTasks')} ({notExecutedInstances.length})
            </h3>
            <div className="space-y-2">
              {notExecutedInstances.map(inst => {
                const isMissed = inst.status === 'missed';
                const isLate = inst.status === 'late';
                const iconClass = isMissed
                  ? 'text-destructive'
                  : isLate
                    ? 'text-warning'
                    : 'text-muted-foreground';
                const badgeVariant: 'destructive' | 'secondary' =
                  isMissed || isLate ? 'destructive' : 'secondary';
                const statusLabel = isMissed
                  ? t('shiftDetail.statusMissed')
                  : isLate
                    ? t('shiftDetail.statusLate')
                    : t('shiftDetail.statusPending');
                return (
                  <Card key={inst.id}>
                    <CardContent className="p-3 flex items-center gap-3">
                      <AlertTriangle className={`w-4 h-4 shrink-0 ${iconClass}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {inst.task?.name || t('shiftDetail.tasks')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(inst.scheduled_for), 'HH:mm')}
                        </p>
                      </div>
                      <Badge variant={badgeVariant}>{statusLabel}</Badge>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Task Executions */}
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-3">
            {t('shiftDetail.executedTasks')}
          </h3>

          {executions.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{t('shiftDetail.noTasks')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {executions.map(execution => (
                <TaskFeedItem key={execution.id} execution={execution} patientId={patientId} isOwner={isOwner} />
              ))}
            </div>
          )}
        </div>

        {/* Photos */}
        {(shift.check_in_photo_url || shift.check_out_photo_url) && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3">
              {t('shiftDetail.photos')}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {shift.check_in_photo_url && (
                <Card
                  className="cursor-pointer"
                  onClick={() => setSelectedPhoto(shift.check_in_photo_url)}
                >
                  <CardContent className="p-2">
                    <SignedImage
                      src={shift.check_in_photo_url}
                      bucket="shift-photos"
                      alt={t('shiftDetail.checkInPhoto')}
                      className="w-full aspect-square object-cover rounded-lg"
                      fallback={
                        <div className="w-full aspect-square flex items-center justify-center bg-muted rounded-lg">
                          <ImageOff className="w-8 h-8 text-muted-foreground" />
                        </div>
                      }
                    />
                    <p className="text-xs text-center text-muted-foreground mt-2">
                      {t('shiftDetail.checkInPhoto')}
                    </p>
                  </CardContent>
                </Card>
              )}
              {shift.check_out_photo_url && (
                <Card
                  className="cursor-pointer"
                  onClick={() => setSelectedPhoto(shift.check_out_photo_url)}
                >
                  <CardContent className="p-2">
                    <SignedImage
                      src={shift.check_out_photo_url}
                      bucket="shift-photos"
                      alt={t('shiftDetail.checkOutPhoto')}
                      className="w-full aspect-square object-cover rounded-lg"
                      fallback={
                        <div className="w-full aspect-square flex items-center justify-center bg-muted rounded-lg">
                          <ImageOff className="w-8 h-8 text-muted-foreground" />
                        </div>
                      }
                    />
                    <p className="text-xs text-center text-muted-foreground mt-2">
                      {t('shiftDetail.checkOutPhoto')}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Photo Viewer Modal */}
      {selectedPhoto && (
        <PhotoViewerModal
          open={!!selectedPhoto}
          imageUrl={selectedPhoto}
          bucket="shift-photos"
          onClose={() => setSelectedPhoto(null)}
        />
      )}
    </AppLayout>
  );
}
